<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <Label class="text-sm font-medium">Inherit from Scene</Label>
    </div>

    <!-- Current Parent Display -->
    <div v-if="currentParent" class="flex items-center gap-2 p-2 rounded-lg border bg-card">
      <div class="flex-shrink-0 w-16 h-10 rounded overflow-hidden bg-muted">
        <ScenePreviewCached
          v-if="primaryDisplay"
          :scene-id="currentParent.id"
          :display="primaryDisplay"
          :max-width="64"
          :max-height="40"
          :eager="true"
        />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium truncate">{{ currentParent.name }}</p>
        <p class="text-xs text-muted-foreground">Current parent</p>
      </div>
      <Button variant="ghost" size="sm" @click="showPicker = true">
        <RefreshCw class="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" @click="handleRemoveParent">
        <X class="w-4 h-4" />
      </Button>
    </div>

    <!-- No Parent - Set Parent Button -->
    <Button 
      v-else
      variant="outline" 
      size="sm" 
      class="w-full justify-start"
      @click="showPicker = true"
    >
      <Link class="w-4 h-4 mr-2" />
      Set Parent Scene
    </Button>

    <!-- Scene Picker Dialog -->
    <Dialog v-model:open="showPicker">
      <DialogContent class="sm:max-w-[500px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Choose Parent Scene</DialogTitle>
          <DialogDescription>
            Select a scene to inherit layers and settings from
          </DialogDescription>
        </DialogHeader>

        <ScrollArea class="h-[400px] pr-4">
          <div class="space-y-2">
            <!-- Option to remove parent -->
            <button
              v-if="currentParent"
              @click="handleRemoveParent"
              class="flex items-center gap-3 w-full p-3 rounded-lg border border-dashed hover:bg-accent transition-colors"
            >
              <div class="w-16 h-10 rounded bg-muted flex items-center justify-center">
                <X class="w-5 h-5 text-muted-foreground" />
              </div>
              <div class="flex-1 text-left">
                <p class="text-sm font-medium">No Parent (Root Scene)</p>
                <p class="text-xs text-muted-foreground">Remove inheritance</p>
              </div>
            </button>

            <!-- Available Scenes -->
            <button
              v-for="scene in availableScenes"
              :key="scene.id"
              @click="handleSelectScene(scene.id)"
              class="flex items-center gap-3 w-full p-3 rounded-lg border hover:bg-accent transition-colors"
              :class="{ 'border-primary bg-primary/5': scene.id === currentParent?.id }"
            >
              <div class="flex-shrink-0 w-16 h-10 rounded overflow-hidden bg-muted">
                <ScenePreviewCached
                  v-if="primaryDisplay"
                  :scene-id="scene.id"
                  :display="primaryDisplay"
                  :max-width="64"
                  :max-height="40"
                  :eager="true"
                />
              </div>
              <div class="flex-1 text-left min-w-0">
                <p class="text-sm font-medium truncate">{{ scene.name }}</p>
                <p class="text-xs text-muted-foreground">
                  {{ scene.hierarchy_level === 0 ? 'Root scene' : `Level ${scene.hierarchy_level}` }}
                </p>
              </div>
              <Check v-if="scene.id === currentParent?.id" class="w-5 h-5 text-primary flex-shrink-0" />
            </button>

            <div v-if="availableScenes.length === 0" class="py-8 text-center text-muted-foreground text-sm">
              No other scenes available
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useScenesStore } from '@/stores/scenes'
import { useDisplaysStore } from '@/stores/displays'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import ScenePreviewCached from '@/components/scenes/ScenePreviewCached.vue'
import { Link, RefreshCw, X, Check } from 'lucide-vue-next'

interface Props {
  sceneId: string
}

const props = defineProps<Props>()

const scenesStore = useScenesStore()
const displaysStore = useDisplaysStore()

const showPicker = ref(false)
const isUpdating = ref(false)

// Get current scene
const currentScene = computed(() => scenesStore.scenes.get(props.sceneId))

// Get current parent scene
const currentParent = computed(() => {
  if (!currentScene.value?.parent_id) return null
  return scenesStore.scenes.get(currentScene.value.parent_id) || null
})

// Get primary display for previews (first active display or first display)
const primaryDisplay = computed(() => {
  const allDisplays = displaysStore.allDisplays
  return allDisplays.find(d => d.is_active) || allDisplays[0] || null
})

// Get available scenes (exclude current scene and its descendants to prevent cycles)
const availableScenes = computed(() => {
  const descendantIds = getDescendantIds(props.sceneId)
  descendantIds.add(props.sceneId) // Also exclude the current scene itself
  
  return Array.from(scenesStore.scenes.values())
    .filter(scene => !descendantIds.has(scene.id))
    .sort((a, b) => {
      // Sort by hierarchy level, then by name
      if (a.hierarchy_level !== b.hierarchy_level) {
        return a.hierarchy_level - b.hierarchy_level
      }
      return a.name.localeCompare(b.name)
    })
})

// Get all descendant scene IDs to prevent cycles
const getDescendantIds = (sceneId: string): Set<string> => {
  const descendants = new Set<string>()
  const children = scenesStore.getChildren(sceneId)
  
  for (const child of children) {
    descendants.add(child.id)
    const childDescendants = getDescendantIds(child.id)
    childDescendants.forEach(id => descendants.add(id))
  }
  
  return descendants
}

// Handle selecting a new parent
const handleSelectScene = async (parentId: string) => {
  if (isUpdating.value) return
  
  isUpdating.value = true
  try {
    await scenesStore.updateSceneParent(props.sceneId, parentId)
    showPicker.value = false
  } catch (error) {
    console.error('Failed to update parent:', error)
  } finally {
    isUpdating.value = false
  }
}

// Handle removing parent (make root scene)
const handleRemoveParent = async () => {
  if (isUpdating.value) return
  
  isUpdating.value = true
  try {
    await scenesStore.updateSceneParent(props.sceneId, null)
    showPicker.value = false
  } catch (error) {
    console.error('Failed to remove parent:', error)
  } finally {
    isUpdating.value = false
  }
}
</script>
